import { useEffect, useState } from "react";
import AdminLayout from "./components/AdminLayout";
import { supabase } from "@/lib/supabase";
import type { Case } from "@/lib/supabase";

export default function CasesAdminPage() {
  const [items, setItems] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Partial<Case> | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    const { data } = await supabase.from("cases").select("*").order("display_order");
    setItems(data ?? []);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const openNew = () => { setEditing({ title: "", description: "", category: "", client: "", year: new Date().getFullYear(), active: true }); setShowModal(true); };
  const openEdit = (c: Case) => { setEditing(c); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setEditing(null); };

  const handleSave = async () => {
    if (!editing?.title) return;
    setSaving(true);
    if (editing.id) {
      await supabase.from("cases").update({ title: editing.title, description: editing.description, challenge: editing.challenge, solution: editing.solution, image_url: editing.image_url, category: editing.category, client: editing.client, year: editing.year, active: editing.active }).eq("id", editing.id);
    } else {
      await supabase.from("cases").insert({ title: editing.title, description: editing.description, challenge: editing.challenge, solution: editing.solution, image_url: editing.image_url, category: editing.category, client: editing.client, year: editing.year, active: editing.active ?? true });
    }
    setSaving(false);
    closeModal();
    fetchData();
  };

  const handleDelete = async (id: string) => {
    await supabase.from("cases").delete().eq("id", id);
    setDeleteId(null);
    fetchData();
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1a1a1a]">Cases / Portfólio</h1>
          <p className="text-gray-500 text-sm mt-1">{items.length} case(s)</p>
        </div>
        <button onClick={openNew} className="flex items-center gap-2 bg-[#1B1F5E] hover:bg-[#2F4F8F] text-white font-bold text-sm px-4 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line"></i></div>
          Novo Case
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40"><div className="w-6 h-6 flex items-center justify-center text-[#1B1F5E]"><i className="ri-loader-4-line animate-spin text-2xl"></i></div></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3"><i className="ri-folder-image-line text-4xl"></i></div>
            <p className="text-sm">Nenhum case cadastrado</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Título</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Cliente</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Categoria</th>
                <th className="text-center text-xs font-semibold text-gray-500 px-4 py-3">Ano</th>
                <th className="text-center text-xs font-semibold text-gray-500 px-4 py-3">Status</th>
                <th className="text-center text-xs font-semibold text-gray-500 px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {items.map(c => (
                <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-[#1a1a1a]">{c.title}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{c.client ?? "—"}</td>
                  <td className="px-4 py-3"><span className="bg-[#1B1F5E]/10 text-[#1B1F5E] text-xs font-medium px-2 py-0.5 rounded-full">{c.category ?? "—"}</span></td>
                  <td className="px-4 py-3 text-center text-sm text-gray-500">{c.year ?? "—"}</td>
                  <td className="px-4 py-3 text-center"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.active ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>{c.active ? "Ativo" : "Inativo"}</span></td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => openEdit(c)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-[#1B1F5E] transition-colors cursor-pointer"><i className="ri-edit-line text-sm"></i></button>
                      <button onClick={() => setDeleteId(c.id)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors cursor-pointer"><i className="ri-delete-bin-line text-sm"></i></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4 overflow-y-auto py-8">
          <div className="bg-white rounded-xl w-full max-w-lg p-6 my-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-[#1a1a1a]">{editing.id ? "Editar Case" : "Novo Case"}</h2>
              <button onClick={closeModal} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-xl"></i></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#333] mb-1.5">Título *</label>
                <input value={editing.title ?? ""} onChange={e => setEditing({ ...editing, title: e.target.value })} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#1B1F5E]" placeholder="Título do case" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#333] mb-1.5">Cliente</label>
                  <input value={editing.client ?? ""} onChange={e => setEditing({ ...editing, client: e.target.value })} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#1B1F5E]" placeholder="Nome do cliente" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#333] mb-1.5">Ano</label>
                  <input type="number" value={editing.year ?? ""} onChange={e => setEditing({ ...editing, year: Number(e.target.value) })} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#1B1F5E]" placeholder="2024" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#333] mb-1.5">Categoria</label>
                <input value={editing.category ?? ""} onChange={e => setEditing({ ...editing, category: e.target.value })} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#1B1F5E]" placeholder="Ex: Automação Industrial" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#333] mb-1.5">Descrição</label>
                <textarea value={editing.description ?? ""} onChange={e => setEditing({ ...editing, description: e.target.value })} rows={2} maxLength={500} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#1B1F5E] resize-none" placeholder="Descrição breve" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#333] mb-1.5">URL da Imagem</label>
                <input value={editing.image_url ?? ""} onChange={e => setEditing({ ...editing, image_url: e.target.value })} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#1B1F5E]" placeholder="https://..." />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={editing.active ?? true} onChange={e => setEditing({ ...editing, active: e.target.checked })} className="w-4 h-4 accent-[#1B1F5E]" />
                <span className="text-sm text-[#333]">Ativo no site</span>
              </label>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={closeModal} className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 bg-[#1B1F5E] hover:bg-[#2F4F8F] text-white font-bold text-sm rounded-lg cursor-pointer whitespace-nowrap disabled:opacity-60">{saving ? "Salvando..." : "Salvar"}</button>
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4"><i className="ri-delete-bin-line text-red-500 text-xl"></i></div>
            <h3 className="text-base font-bold text-[#1a1a1a] mb-2">Excluir case?</h3>
            <p className="text-gray-500 text-sm mb-6">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={() => handleDelete(deleteId)} className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white font-bold text-sm rounded-lg cursor-pointer whitespace-nowrap">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
